import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cx, focusRingClass } from './shared';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  wrapperClassName?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      id,
      className,
      wrapperClassName,
      'aria-describedby': ariaDescribedBy,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const checkboxId = id ?? `checkbox-${generatedId}`;
    const descriptionId = description ? `${checkboxId}-description` : undefined;
    const errorId = error ? `${checkboxId}-error` : undefined;
    const describedBy = [ariaDescribedBy, descriptionId, errorId].filter(Boolean).join(' ');

    return (
      <div className={cx('grid gap-1', wrapperClassName)}>
        <label
          htmlFor={checkboxId}
          className="flex min-h-control-comfortable cursor-pointer items-start gap-field-gap rounded-control text-body text-ink"
        >
          <input
            ref={ref}
            id={checkboxId}
            type="checkbox"
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy || undefined}
            className={cx(
              focusRingClass,
              'mt-3 h-5 w-5 shrink-0 cursor-pointer rounded-control border-border-strong accent-accent disabled:cursor-not-allowed',
              className,
            )}
            {...rest}
          />
          <span className="py-2.5">
            <span className="block font-semibold text-ink-strong">{label}</span>
            {description ? (
              <span id={descriptionId} className="mt-1 block text-meta text-ink-muted">
                {description}
              </span>
            ) : null}
          </span>
        </label>
        {error ? (
          <p id={errorId} className="text-meta font-semibold text-status-invalid">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
